import { moment } from 'localization'
import capitalizeWords from '../utils/capitalizeWords'
import capitalizeSentence from 'utils/capitalizeSentence'

import { i18n } from 'localization'
import { parseDate, parsePerson } from './utils'
import ClinicalEpisode from './ClinicalEpisode'

const segmentColor = {
	AT: '#9960C5',
	AD: null,
	AP: null,
	AV: null,
	RC: null,
	VD: null,
}

export default class ClinicalProcess {
	static fromJSON(json) {
		if (!json) return null
		return (
			new ClinicalProcess(json.id)
				//TODO: info must be the source of bodyPart and injury (mainMedicalDiagnosis is temporary)
				.withInfo(
					json.info,
					json.mainMedicalDiagnosis?.bodyPartId?.toString(),
					json.mainMedicalDiagnosis?.injuryId?.toString(),
					json.bodyPartCode?.toString()
				)
				.withInjured(json.injured)
				.withAccident(json.accident)
				.withClinicalStatus(json.clinicalStatus)
				.withStatus(json.statusHistory)
				.withState(json.clinicalStatus)
				.withClub(json.club)
				.withInsurancePolicy(
					json.insurancePolicy,
					json.insurancePolicyAdDetails,
					json.clubId
				)
				.withCompanyManager(json.claimsManager, json.claimsManagerId)
				.withTemporaryProcessNumber(json.temporaryProcessId)
				.withTrustManager(json.claimsManagerTrust)
				.withClinicCoordinator(json.clinicCoordinator)
				.withInitialCoordinator(json.initialClinicCoordinatorUser)
				.withIPPCoordinator(json.claimsManagerAnalise)
				.withMainMedicalDiagnosis(json.mainMedicalDiagnosis)
				.withMedicalDiagnosis(json.medicalDiagnosis)
				.withProvider(json.providerEntity)
				.withObservations(json.observations)
				.withForwardUrgency(json.forwardUrgencyId)
				.withTemporaryProvider(json.temporaryProviderEntity)
				.withClinicalEpisodes(json.clinicalEpisodes)
				.withProcessCategory(json.processCategory)
				.withProcessInjuredState(json.processInjuredStateId)
				.withManualRequestUser(json.manualRequestUser)
				.withManualPrevisionObservation(json.manualPrevisionObservation)
				.withManualIPPRequest(json.manualIPPRequest)
				.withHasPreviousSurgery(json.hasPreviousSurgery)
				.withTag(json.processTag)
				.withProcessTags(json.processTags)
				.withPlafond(json.plafond)
				.withLink(json)
		)
	}

	constructor(id) {
		this.id = id
		this.key = `${id}`
	}

	withInfo(json = {}, bodyPartId, injuryTypeId, bodyPartCode) {
		const createdAtTime = json.createdAt
			? moment(new Date(json.createdAt))
			: null

		const hasCausalLink =
			json.causalLink !== undefined && json.causalLink !== null
				? !!json.causalLink
				: undefined
		const causalLink =
			json.causalLink === undefined || json.causalLink === null
				? 'missing'
				: json.causalLink
				? 'valid'
				: 'invalid'
		const isCausalLinkDubious = json.isCausalLinkDubious

		this.info = json
			? {
					...json,
					bodyPartId: (
						json.bodyPart?.id ||
						json.bodyPartId ||
						bodyPartId ||
						''
					).toString?.(),
					bodyPartName: json.bodyPart?.name || '',
					bodyPartCode:
						json.bodyPart?.code ||
						json.bodyPartCode ||
						bodyPartCode,
					injuryTypeId: (
						json.injuryType?.id ||
						json.injuryTypeId ||
						injuryTypeId ||
						''
					).toString?.(),
					injuryTypeName: json.injuryType?.name || '',
					hasCausalLink,
					causalLink,
					isCausalLinkDubious,
					admissionDate: parseDate(json.admissionDate),
					createdAt: parseDate(createdAtTime),
					biologicalRisk: json?.biologicalRisk,
					managementType: json?.managementType,
					adminProcess: json?.adminProcess,
			  }
			: null
		return this
	}

	withInjured(json) {
		this.injured = parsePerson(json)
		return this
	}

	withAccident(json) {
		this.accident = json
			? {
					date: parseDate(json.date),
					local: json.accidentSite?.description
						? i18n.translate(json.accidentSite.description)
						: '',
					localId: json.accidentSite?.id,
					description: capitalizeSentence(json.description),
					segment: json.segment
						? {
								...json.segment,
								abbreviation: json.segment.abbreviation
									? i18n.translate(
											`Segment_${json.segment.abbreviation.trim()}`
									  )
									: '',
								name: json.segment.name
									? i18n.translate(json.segment.name.trim())
									: '',
								color: segmentColor[
									json.segment.abbreviation?.trim()
								],
						  }
						: null,
					occupation: capitalizeWords(json.occupation),
					isPensioner: json.isPensioner,
					pensionerDate: json.pensionerDate
						? parseDate(json.pensionerDate)
						: null,
					status: json.status
						? { description: json.status }
						: undefined,
			  }
			: null
		return this
	}

	withClinicalStatus(json) {
		this.clinicalStatus = json?.current?.value
			? {
					value: json.current.value,
					description: json.current.description,
			  }
			: null
		return this
	}

	withStatus(json) {
		this.status = json?.current?.value
			? {
					value: json.current.value,
					description: json.current.description,
					icon: json.current.value.toLowerCase().replace('_', '-'),
			  }
			: null
		return this
	}

	withState(json) {
		if (!json?.current?.value) {
			this.state = null
			return this
		}
		const current = json.current.value

		const stateOrder = ['ON_HOLD', 'ON_GOING', 'EXECUTED']
		const currentIdx = stateOrder.indexOf(current)

		const progresses = {
			ON_HOLD: [0, 0],
			ON_GOING: [1, 0],
			EXECUTED: [1, 1],
		}
		const steps = {
			ON_HOLD: [1, 0, 0],
			ON_GOING: [0, 1, 0],
			EXECUTED: [0, 0, 1],
		}
		const stateDescriptions = {
			ON_HOLD: 'Pendente',
			ON_GOING: 'Tratamento',
			EXECUTED: 'Alta',
		}

		this.state = {
			currentState: steps[current] || [0, 0, 0],
			progress: progresses[current] || [0, 0],
			executed: {
				onHold: currentIdx >= 0,
				onGoing: currentIdx >= 1,
				executed: currentIdx >= 2,
			},
			stateLabel: json.current.description || '',
			labels: stateOrder.map((s) => stateDescriptions[s]),
		}
		return this
	}

	withClub(json) {
		this.club = { ...json }

		return this
	}

	withInsurancePolicy(json, ADJson) {
		if (json) {
			this.insurancePolicy = {
				policyNumber: json.policyNumber,
				externalId: json.externalId,
				insuranceCompany: {
					id: json.insuranceCompany?.id,
					name: json.insuranceCompany?.name,
					shortName: json.insuranceCompany?.shortName,
					internalName: json.insuranceCompany?.internalName,
				},
				insured: json.insured
					? {
							name: json.insured.name,
					  }
					: undefined,
				AdDetails: ADJson
					? {
							club: ADJson.club,
							policyConditions: {
								franchise: ADJson.franchise,
								plafond: ADJson.plafond,
							},
					  }
					: undefined,
			}
		}

		return this
	}

	withCompanyManager(json, claimsManagerId) {
		this.companyClaimsManager = {
			...parsePerson(json),
			id: claimsManagerId,
		}
		return this
	}

	withTrustManager(json) {
		this.trustClaimsManager = parsePerson(json)
		return this
	}

	withClinicCoordinator(json) {
		this.clinicCoordinator = parsePerson(json)
		return this
	}

	withInitialCoordinator(json) {
		this.initialCoordinator = parsePerson(json)
		return this
	}

	withIPPCoordinator(json) {
		this.ippCoordinator = parsePerson(json)
		return this
	}

	withBodyPartAndInjury(bodyPartId, injuryId) {
		this.bodyPartId = bodyPartId
		this.injuryId = injuryId
		return this
	}

	withMainMedicalDiagnosis(json) {
		this.mainMedicalDiagnosis = json
			? {
					icd9Code: json.icd9Code,
					icd9Diagnostic: json.icd9Diagnostic,
					bodyPartName: i18n.translate(json.bodyPartName),
					bodyPartId: json.bodyPartId?.toString(),
					bodyPart: {
						id: json.bodyPartId?.toString(),
						text: i18n.translate(json.bodyPartName),
					},
			  }
			: null
		return this
	}
	withMedicalDiagnosis(medicalDiagnosis) {
		this.medicalDiagnosis = medicalDiagnosis
		return this
	}

	withProvider(json) {
		this.provider = json
			? {
					id: json.id,
					name: json.name,
			  }
			: null
		return this
	}

	withObservations(observations) {
		this.observations = observations
		return this
	}

	withForwardUrgency(forwardUrgencyId) {
		this.forwardUrgencyId = forwardUrgencyId
		return this
	}

	withTemporaryProvider(json) {
		this.temporaryProvider = json
			? {
					id: json.id,
					name: json.name,
			  }
			: null
		return this
	}

	withClinicalEpisodes(clinicalEpisodes) {
		this.clinicalEpisodes = (clinicalEpisodes || []).map((json) =>
			ClinicalEpisode.fromJSON(json)
		)
		this.lastClinicalEpisode =
			this.clinicalEpisodes[this.clinicalEpisodes.length - 1]
		return this
	}

	withLink(json) {
		if (json?.id) {
			this.link = {
				to: `/clinical-processes/${json?.id}/appointments`,
				name: i18n`See clinical process`,
			}
		}
		return this
	}

	withTemporaryProcessNumber(temporaryProcessId) {
		this.temporaryProcessId = temporaryProcessId
		return this
	}

	withProcessCategory(processCategory) {
		this.processCategory = processCategory
		return this
	}

	withProcessInjuredState(processInjuredStateId) {
		this.processInjuredState = processInjuredStateId
		return this
	}

	withManualRequestUser(manualRequestUser) {
		this.manualRequestUser = manualRequestUser
			? parsePerson(manualRequestUser)
			: null
		return this
	}

	withManualPrevisionObservation(manualPrevisionObservation) {
		this.manualPrevisionObservation = manualPrevisionObservation
		return this
	}

	withManualIPPRequest(manualIPPRequest) {
		this.manualIPPRequest = manualIPPRequest
		return this
	}

	withTag(processTag) {
		this.tag = processTag
		return this
	}

	withPlafond(plafond) {
		this.plafond = plafond
		return this
	}

	withHasPreviousSurgery(hasPreviousSurgery) {
		this.hasPreviousSurgery = hasPreviousSurgery
		return this
	}

	withProcessTags(processTags) {
		this.processTags = processTags || []
		return this
	}
}
